import { useState } from "react"
import type { CSSProperties, FC } from "react"
import { Tooltip } from "antd"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import chevronDownIcon from "assets/icons/quotation-viewer/chevron-down.svg"
import { CheckmarkIcon } from "components/ui/icons/quotations/viewer/CanvasToolIcons"
import { LINE_STYLE_OPTIONS } from "../../interactions/figureHighlight.constants"
import type { TLineStyleOption } from "../../interactions/figureHighlight.constants"
import { compactTooltipStyles, getShadowTooltipContainer } from "../shadowTooltip"
import styles from "./LineStylePicker.module.scss"

type Props = {
	value: string
	onChange: (value: string) => void
	/**
	 * Line-style values to leave OUT of the list. Exists for one real case: a cloudy border is a
	 * genuine Apryse border effect on a rectangle but is unimplemented for an ellipse on this SDK
	 * build, where picking it would persist happily and then draw a plain solid curve. Hiding beats
	 * disabling — a greyed row invites "why?", and the answer ("the SDK can't draw it for this shape")
	 * is not something an estimator can act on.
	 */
	hiddenValues?: readonly string[]
}

const popoverStyle: CSSProperties = {
	display: "flex",
	flexDirection: "column",
	width: 140,
	padding: 4,
	background: "#fff",
	border: "1px solid var(--clr-lightGray, #dee0eb)",
	borderRadius: 8,
	boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
}

const rowStyle = (selected: boolean): CSSProperties => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: 8,
	width: "100%",
	padding: "6px 8px",
	border: "none",
	borderRadius: 6,
	background: selected ? "var(--clr-superLightGray, #f2f3f7)" : "transparent",
	cursor: "pointer",
})

/**
 * Line-style dropdown (REQ-400 §1381 review, Figma "Rectangle / Line style / Opened", node
 * 19019:672610's linked design): an icon + a preview of the current pattern + chevron trigger,
 * opening a vertical list of the 7 catalogued patterns (`figureHighlight.constants`'s
 * `LINE_STYLE_OPTIONS`) with the active one checked.
 *
 * Same `popupRender` + fully-inline-styles shape as `ColorSwatchPicker` / `SliderPopover` — see
 * either's comment for why (a popup can portal outside this stylesheet's shadow root; the row
 * previews use inline SVG, which travels with the subtree regardless of where it lands, so this one
 * doesn't even need the `<style>`-tag trick the slider's pseudo-element thumb needed).
 */
export const LineStylePicker: FC<Props> = ({ value, onChange, hiddenValues }) => {
	const [open, setOpen] = useState(false)
	const options = hiddenValues?.length
		? LINE_STYLE_OPTIONS.filter((option) => !hiddenValues.includes(option.value))
		: LINE_STYLE_OPTIONS
	// The TRIGGER resolves against the full catalogue, not the filtered list: a hidden style can still
	// be the live value for the instant before the caller resets it, and falling back to "solid" here
	// would show the wrong preview during that frame.
	const selected = LINE_STYLE_OPTIONS.find((option) => option.value === value) ?? LINE_STYLE_OPTIONS[0]

	const linePreview = (dasharray: string, width = 24) => (
		<svg width={width} height={8} viewBox={`0 0 ${width} 8`} aria-hidden="true">
			<line
				x1={2}
				y1={4}
				x2={width - 2}
				y2={4}
				stroke="currentColor"
				strokeWidth={1.5}
				strokeDasharray={dasharray || undefined}
				strokeLinecap={dasharray.startsWith("0.5") ? "round" : "butt"}
			/>
		</svg>
	)

	// "cloudy" (#1386 review) is the one catalogued style a `stroke-dasharray` cannot express — it's a
	// scalloped border, so it gets its own preview path rather than a dash pattern faking it. The
	// scallops are generated from the available width so the same helper serves both the narrow
	// trigger (24) and the wide popover rows (96), and the arc radius is derived from the step so the
	// bump chain stays vertically centred on y=4 in the shared height-8 viewBox (bumps span
	// `baseline - r` .. `baseline`, so `baseline = 4 + r / 2` puts their midline exactly on 4).
	const cloudyPreview = (width = 24) => {
		const start = 2
		const span = width - start * 2
		const bumpCount = Math.max(2, Math.round(span / 6))
		const step = span / bumpCount
		const radius = step / 2
		const baseline = 4 + radius / 2
		let path = `M ${start} ${baseline}`
		for (let index = 1; index <= bumpCount; index += 1) {
			path += ` A ${radius} ${radius} 0 0 1 ${start + step * index} ${baseline}`
		}
		return (
			<svg width={width} height={8} viewBox={`0 0 ${width} 8`} aria-hidden="true">
				<path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" />
			</svg>
		)
	}

	const optionPreview = (option: TLineStyleOption, width?: number) =>
		option.preview === "cloudy" ? cloudyPreview(width) : linePreview(option.dasharray, width)

	return (
		<Dropdown
			items={[]}
			placement="bottomLeft"
			open={open}
			onOpenChange={setOpen}
			trigger={["click"]}
			overlayStyle={{ zIndex: 1500 }}
			popupRender={() => (
				<div style={popoverStyle}>
					{options.map((option) => (
						<button
							key={option.value}
							type="button"
							style={rowStyle(option.value === value)}
							onClick={() => {
								onChange(option.value)
								setOpen(false)
							}}
						>
							{optionPreview(option, 96)}
							{option.value === value ? (
								<span style={{ width: 14, height: 14, color: "var(--clr-main, #0047ff)" }}>
									<CheckmarkIcon />
								</span>
							) : (
								<span style={{ width: 14 }} />
							)}
						</button>
					))}
				</div>
			)}
		>
			<Tooltip title="Line style" placement="bottom" getPopupContainer={getShadowTooltipContainer} styles={compactTooltipStyles}>
				<button type="button" className={styles.trigger} aria-label="Line style">
					<span className={styles.preview}>{optionPreview(selected)}</span>
					<img className={styles.chevron} src={chevronDownIcon} alt="" />
				</button>
			</Tooltip>
		</Dropdown>
	)
}
